import { Button, alpha, keyframes, styled } from '@mui/material';
import type { Theme } from '@mui/material';

export type PressableTone = 'blue' | 'gold' | 'paper' | 'plain';

/** A CTA that carries a trailing credit pill: the label sits left, the pill
 * right, and the right padding tightens so the pill hugs the edge. Lives here
 * rather than in `sx` because `SHELL`'s `&&&` centring would out-specify it. */
const SPREAD = {
  justifyContent: 'space-between',
  padding: '0 8px 0 20px',
} as const;

/** The raised tones read off the theme, so a palette change moves the CTAs with
 * everything else. `fg` is the palette's own contrast text: these labels sit on
 * a coloured fill, so they must not flip with the mode the way neutrals do.
 * `ring` outlines a fill that would otherwise vanish into the surface. */
const tones = (
  theme: Theme,
): Record<
  Exclude<PressableTone, 'plain'>,
  { bg: string; fg: string; shade: string; glow: string; ring?: string }
> => ({
  blue: {
    bg: theme.palette.secondary.main,
    fg: theme.palette.secondary.contrastText,
    shade: theme.palette.secondary.dark,
    glow: alpha(theme.palette.secondary.main, 0.26),
  },
  gold: {
    bg: theme.palette.brand.gold,
    fg: theme.palette.brand.onGold,
    shade: theme.palette.brand.goldDeep,
    glow: alpha(theme.palette.brand.gold, 0.28),
  },
  // The raised partner to a coloured CTA: the same depth, without competing
  // for attention. Its fill is the surface's own, so its label follows the
  // mode with it.
  paper: {
    bg: theme.palette.background.paper,
    fg: theme.palette.text.primary,
    shade: theme.palette.divider,
    glow: alpha(theme.palette.text.primary, 0.06),
    ring: theme.palette.divider,
  },
});

/** The design's `.goldPill` motion: a slow halo that breathes out and fades,
 * and a highlight that sweeps across. Both read the button's own shadow and
 * glow through custom properties so one pair of keyframes serves every tone. */
const glowPulse = keyframes`
  0%, 100% { box-shadow: var(--pressable-raised), 0 0 0 0 var(--pressable-glow); }
  50% { box-shadow: var(--pressable-raised), 0 0 0 7px transparent; }
`;

const sheenSweep = keyframes`
  0% { transform: translateX(-140%) skewX(-18deg); }
  55%, 100% { transform: translateX(320%) skewX(-18deg); }
`;

/** Shared geometry — the raised tones and the flat one must line up in a grid. */
const SHELL = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '9px',
  borderRadius: '999px',
  textTransform: 'none' as const,
  whiteSpace: 'nowrap' as const,
};

/**
 * The chunky "pressable" CTA from the design files: the offset shadow *is* the
 * button's depth, so pressing trades shadow height for translate rather than
 * animating a separate layer.
 *
 * `tone="plain"` is the exception — a flat outlined button for actions that are
 * over (a finished competition's "See results"). It has no depth to collapse,
 * so it does not travel on press; it tints instead.
 *
 * The `&&&` is load-bearing. The theme sets `&.MuiButton-contained {
 * box-shadow: none }`, which is two classes and out-specifies a plain
 * `styled()` rule — without the extra specificity the depth silently
 * disappears and the button renders flat.
 */
const PressableButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== 'tone' &&
    prop !== 'depth' &&
    prop !== 'pulse' &&
    prop !== 'spread',
})<{
  tone?: PressableTone;
  depth?: number;
  pulse?: boolean;
  spread?: boolean;
}>(({ theme, tone = 'blue', depth = 5, pulse = false, spread = false }) => {
  if (tone === 'plain') {
    return {
      '&&&': {
        ...SHELL,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
        transition: 'background-color 0.16s ease, box-shadow 0.16s ease',
        '&:hover': {
          backgroundColor: theme.palette.background.default,
          boxShadow: `inset 0 0 0 1px ${theme.palette.text.disabled}`,
        },
        '&:active': { backgroundColor: theme.palette.action.selected },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.secondary,
          boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
        },
      },
    };
  }

  const { bg, fg, shade, glow, ring } = tones(theme)[tone];
  const outline = ring ? `inset 0 0 0 1px ${ring}, ` : '';
  const raised = `${outline}0 ${depth}px 0 ${shade}, 0 ${depth * 3}px ${depth * 6}px ${glow}`;

  return {
    '&&&': {
      ...SHELL,
      ...(spread && SPREAD),
      backgroundColor: bg,
      color: fg,
      boxShadow: raised,
      transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.15s',
      ...(pulse && {
        position: 'relative',
        overflow: 'hidden',
        '--pressable-raised': raised,
        '--pressable-glow': glow,
        // Only while it is actionable -- a disabled CTA that keeps pulsing
        // reads as if it were still asking to be pressed.
        '&:not(.Mui-disabled)': {
          animation: `${glowPulse} 2.4s ease-in-out infinite`,
        },
        '&:not(.Mui-disabled)::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: '34%',
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
          animation: `${sheenSweep} 3.2s ease-in-out infinite`,
          pointerEvents: 'none',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '&:not(.Mui-disabled)': { animation: 'none' },
          '&:not(.Mui-disabled)::after': { animation: 'none', opacity: 0 },
        },
      }),
      '&:hover': {
        backgroundColor: bg,
        boxShadow: raised,
        filter: 'brightness(1.04)',
      },
      '&:active': {
        transform: `translateY(${depth - 1}px)`,
        boxShadow: `${outline}0 1px 0 ${shade}`,
        animation: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.disabled,
        boxShadow: 'none',
        cursor: 'not-allowed',
      },
    },
  };
});

export default PressableButton;
